import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { DEFAULT_PROFILE_COMPLETION_OVERLAY } from "../../constants/adminSettings";

const OVERLAY_TYPE_OPTIONS = [
  {
    value: "simple",
    label: "Simple",
    description:
      "Shows a skippable overlay prompting the user to complete their profile.",
  },
  {
    value: "forced",
    label: "Forced",
    description:
      "Shows a blocking overlay that requires profile completion before continuing.",
  },
];

export default function EditProfileCompletionOverlayDialog({
  open,
  onClose,
  settingValue,
  isSaving = false,
  onSave,
}) {
  const [enabled, setEnabled] = useState(
    DEFAULT_PROFILE_COMPLETION_OVERLAY.showProfileCompletionOverlay,
  );
  const [overlayType, setOverlayType] = useState(
    DEFAULT_PROFILE_COMPLETION_OVERLAY.overlayType,
  );

  useEffect(() => {
    if (!open) return;

    setEnabled(
      Boolean(
        settingValue?.showProfileCompletionOverlay ??
          DEFAULT_PROFILE_COMPLETION_OVERLAY.showProfileCompletionOverlay,
      ),
    );
    setOverlayType(
      settingValue?.overlayType === "forced" ? "forced" : "simple",
    );
  }, [open, settingValue]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = () => {
    onSave({
      showProfileCompletionOverlay: enabled,
      overlayType,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Profile Completion Overlay</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  disabled={isSaving}
                  color={enabled ? "success" : "primary"}
                />
              }
              label={
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {enabled ? "Enabled" : "Disabled"}
                </Typography>
              }
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", ml: 1 }}
            >
              When enabled, users with an incomplete profile will see the
              selected overlay.
            </Typography>
          </Box>

          <FormControl fullWidth disabled={!enabled || isSaving}>
            <InputLabel id="overlay-type-label">Overlay Type</InputLabel>
            <Select
              labelId="overlay-type-label"
              value={overlayType}
              label="Overlay Type"
              onChange={(event) => setOverlayType(event.target.value)}
              renderValue={(value) =>
                OVERLAY_TYPE_OPTIONS.find((option) => option.value === value)
                  ?.label ?? value
              }
            >
              {OVERLAY_TYPE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ whiteSpace: "normal", alignItems: "flex-start" }}
                >
                  <ListItemText
                    primary={option.label}
                    secondary={option.description}
                    primaryTypographyProps={{ fontWeight: 600 }}
                    secondaryTypographyProps={{ sx: { whiteSpace: "normal" } }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} thickness={6} /> : null
          }
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
